import React from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Box, Typography,
    IconButton, TextField
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { DailyReportSubcontract, ResourceType } from '../types/subcontractTypes';
import { formatRUT } from '../utils/validation';

interface SubcontractResourceTableProps {
    rows: DailyReportSubcontract[];
    resourceType: ResourceType;
    /** 'view' deja la tabla en solo lectura, igual que el resto del editor. */
    status?: string;
    onChangeHeadcount: (rowIndex: number, value: number) => void;
    onChangeTotalHours: (rowIndex: number, value: number) => void;
    onDelete: (rowIndex: number) => void;
}

/**
 * Sub-tabla de recursos subcontratados dentro de un frente de trabajo.
 *
 * Se usa tanto en el Área de Personal como en el Área de Equipos; lo único que cambia son
 * los rótulos de las dos columnas numéricas. Se mantiene separada de las tablas de recursos
 * propios por la lógica de aislamiento de la sección 4.1 del documento, y con cabecera en
 * tono ámbar para distinguirla visualmente.
 */
const SubcontractResourceTable: React.FC<SubcontractResourceTableProps> = ({
    rows,
    resourceType,
    status,
    onChangeHeadcount,
    onChangeTotalHours,
    onDelete
}) => {
    const isPersonnel = resourceType === 'PERSONNEL';
    const readOnly = status === 'view';

    // Las lineas vacias no se muestran: si no hay subcontratos en el frente, la sub-tabla
    // completa desaparece en lugar de dejar una cabecera sin filas.
    if (rows.length === 0) {
        return null;
    }

    const headcountLabel = isPersonnel ? 'Dotación' : 'Equipos';
    const hoursLabel = isPersonnel ? 'Suma HH' : 'Suma HM';

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#8a6d00', mb: 0.5 }}>
                {isPersonnel ? 'PERSONAL SUBCONTRATADO' : 'EQUIPOS SUBCONTRATADOS'}
            </Typography>
            <TableContainer sx={{ overflowX: 'auto', width: '100%' }}>
                <Table sx={{ minWidth: '100%' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ borderTopLeftRadius: 10, backgroundColor: '#f0c04a', color: '#5c4700', fontWeight: 'bold' }}>
                                RUT / Empresa
                            </TableCell>
                            <TableCell sx={{ backgroundColor: '#f0c04a', color: '#5c4700', fontWeight: 'bold' }}>
                                Cantidades a Reportar
                            </TableCell>
                            {!readOnly && (
                                <TableCell sx={{ borderTopRightRadius: 10, backgroundColor: '#f0c04a', color: '#5c4700', fontWeight: 'bold' }}>
                                    Acción
                                </TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row, index) => (
                            <TableRow key={row.id || `${row.subcontractor.id}-${index}`} sx={{ backgroundColor: 'white' }}>
                                <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#8a6d00' }}>
                                        {row.subcontractor.businessName}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        {formatRUT(row.subcontractor.rut)}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Typography variant="caption">{headcountLabel}:</Typography>
                                            {readOnly ? (
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{row.headcount}</Typography>
                                            ) : (
                                                <TextField
                                                    value={row.headcount === 0 ? '' : row.headcount}
                                                    onChange={(e) => onChangeHeadcount(index, Number(e.target.value))}
                                                    type="number"
                                                    size="small"
                                                    inputProps={{ min: 0, step: 1 }}
                                                    sx={{ width: '80px' }}
                                                />
                                            )}
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Typography variant="caption">{hoursLabel}:</Typography>
                                            {readOnly ? (
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{row.totalHours}</Typography>
                                            ) : (
                                                <TextField
                                                    value={row.totalHours === 0 ? '' : row.totalHours}
                                                    onChange={(e) => onChangeTotalHours(index, Number(e.target.value))}
                                                    type="number"
                                                    size="small"
                                                    inputProps={{ min: 0, step: 'any' }}
                                                    sx={{ width: '90px' }}
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                </TableCell>
                                {!readOnly && (
                                    <TableCell>
                                        <IconButton onClick={() => onDelete(index)} color="primary">
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default SubcontractResourceTable;
